import { CircleCheck, Loader2, RotateCcw, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  type JourneyScreen,
  MobilePreview,
} from '@/features/workflows/components/simple/MobilePreview';
import type { WelcomePreviewStep } from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';

import { useGetCustomization } from '../api/useGetCustomization';
import {
  useGetWorkflowCustomization,
  useSaveWorkflowCustomization,
} from '../api/useWorkflowCustomization';
import { useGoogleFont } from '../hooks/useGoogleFont';
import {
  type CustomizationSettings,
  DEFAULT_CUSTOMIZATION,
  parseCustomization,
  serializeCustomization,
  toBrandCssVars,
} from '../schemas/customizationSchemas';
import { CustomizationFields } from './CustomizationFields';
import { OrganizationLogoField } from './OrganizationLogoField';

// Per-workflow Customization drawer, opened from the builder header (designer:
// react-new-html `CustomizationDrawer`). Scoped to THE open workflow: Save writes
// the workflow's override, Reset clears it so the workflow inherits the
// organization default again — which is how one tenant runs several differently
// themed workflows.
//
// Left: Branding intro, org logo, and the three colour fieldsets. Right: the
// SAME phone preview the builder shows, driven by this workflow's own steps and
// live-themed from the edits in progress.
export function WorkflowCustomizationDrawer({
  isOpen,
  onClose,
  workflowId,
  workflowName,
  readOnly = false,
  journey,
  welcomeSteps = [],
}: {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
  readOnly?: boolean;
  /** The workflow's journey screens, so the preview shows THIS workflow. */
  journey?: JourneyScreen[];
  /** The workflow's steps for the welcome overview. */
  welcomeSteps?: WelcomePreviewStep[];
}) {
  const { data: orgData } = useGetCustomization();
  const wfQuery = useGetWorkflowCustomization(isOpen ? workflowId : null);
  const saveWorkflow = useSaveWorkflowCustomization();

  const [settings, setSettings] = useState<CustomizationSettings>(DEFAULT_CUSTOMIZATION);
  const [dirty, setDirty] = useState(false);
  const [previewScreen, setPreviewScreen] = useState(0);

  // Load the picked Google Font so the preview really renders in it — otherwise
  // the typography change silently falls back to sans-serif.
  useGoogleFont(settings.typographyStyle);

  // Seed the working copy from the workflow's override, falling back to the
  // org default (same resolution the applicant session uses).
  useEffect(() => {
    if (dirty || !isOpen || !wfQuery.data) return;
    const base = wfQuery.data.customizationJson ?? orgData?.customizationJson ?? null;
    setSettings(parseCustomization(base));
  }, [dirty, isOpen, wfQuery.data, orgData]);

  // Re-seed fresh on every open so a previous visit's abandoned edits don't leak.
  useEffect(() => {
    if (isOpen) setDirty(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const isSaving = saveWorkflow.isPending;
  const hasOverride = wfQuery.data?.customizationJson != null;

  function patch<K extends keyof CustomizationSettings>(
    key: K,
    value: Partial<CustomizationSettings[K]> | CustomizationSettings[K],
  ) {
    setSettings((prev) => {
      const current = prev[key];
      const next =
        typeof current === 'object' && current !== null
          ? { ...current, ...(value as object) }
          : (value as CustomizationSettings[K]);
      return { ...prev, [key]: next };
    });
    setDirty(true);
  }

  // Clear the override → this workflow inherits the org default again.
  function handleReset() {
    saveWorkflow.mutate(
      { workflowId, customizationJson: null },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success('Reverted to the organization default.');
        },
        onError: () => toast.error('Could not reset. Please try again.'),
      },
    );
  }

  function handleSave() {
    saveWorkflow.mutate(
      { workflowId, customizationJson: serializeCustomization(settings) },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success('Branding saved for this workflow.');
        },
        onError: () => toast.error('Could not save branding. Please try again.'),
      },
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside className="fixed top-0 end-0 z-40 flex h-dvh w-full max-w-[min(1020px,100vw)] flex-col overflow-hidden bg-white shadow-elevation-2 animate-in slide-in-from-right-4 duration-200">
        {/* Header — title, Reset / Save actions, close. */}
        <div className="flex shrink-0 items-center justify-between gap-3 px-6 py-4">
          <div className="min-w-0">
            <h2 className="m-0 truncate text-xl font-bold text-black-logo">Customization</h2>
            <p className="m-0 truncate text-sm text-gray66">{workflowName}</p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {!readOnly && (
              <>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={isSaving || (!hasOverride && !dirty)}
                  className="flex items-center gap-1.5 rounded-lg border border-primary bg-white px-3 py-1.5 text-base font-semibold text-primary transition-colors hover:bg-[#F0F5FF] disabled:opacity-50"
                >
                  <RotateCcw size={16} className="shrink-0" />
                  <span>Reset</span>
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || !dirty}
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-base font-semibold text-white transition-colors hover:bg-[#003cd6] disabled:opacity-50"
                >
                  {isSaving ? (
                    <Loader2 size={16} className="shrink-0 animate-spin" />
                  ) : (
                    <CircleCheck size={16} className="shrink-0" />
                  )}
                  <span>Save Customization</span>
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-grayEF text-gray66 transition-colors hover:bg-[#ECEEF3]"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body — left controls (scroll) + right live preview (fixed width). */}
        <div className="flex min-h-0 flex-1 gap-4 overflow-hidden px-6 pb-6">
          <fieldset
            disabled={readOnly}
            className="flex min-w-0 flex-1 flex-col gap-5 overflow-y-auto custom-scrollbar"
          >
            <div>
              <h3 className="m-0 mb-1 text-lg font-semibold text-black-logo">Branding</h3>
              <p className="m-0 text-sm text-gray66">
                Customize your brand colors and preview changes in real time to create a consistent
                verification experience.
              </p>
            </div>

            <OrganizationLogoField
              logoUrl={orgData?.logoUrl ?? null}
              disabled={readOnly}
              showPreviewBox={false}
              scopeNote="The logo is shared across your organization — changing or removing it here affects every workflow, and takes effect immediately. Reset restores this workflow's colours only, never the logo."
            />

            {wfQuery.isLoading ? (
              <div className="flex flex-1 items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <CustomizationFields settings={settings} onPatch={patch} />
            )}
          </fieldset>

          {/* Live preview — the same phone the builder shows, themed by the edits
              in progress, so "different workflow, different theme" is visible
              before saving. */}
          <div
            className="hidden shrink-0 overflow-hidden rounded-xl lg:block"
            style={{ width: 'clamp(300px,36vw,480px)' }}
          >
            <MobilePreview
              screen={previewScreen}
              onScreenChange={setPreviewScreen}
              journey={journey}
              welcomeSteps={welcomeSteps}
              logoUrl={orgData?.logoUrl ?? null}
              brandStyle={toBrandCssVars(settings)}
            />
          </div>
        </div>
      </aside>
    </>
  );
}
